/**
 * Soleero product gallery
 *
 * Progressive enhancement for the custom product media gallery. The Liquid
 * markup remains readable without JavaScript; this controller adds selection,
 * variant-media synchronisation, mobile snapping, thumbnail overflow controls,
 * the full-screen mixed-media viewer, and image zoom/pan gestures.
 */
(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 1024px)';
  const TABLET_THUMBNAILS_QUERY = '(min-width: 769px) and (max-width: 1024px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const SELECTOR = {
    stage: '[data-hz-gallery-stage]',
    track: '[data-hz-gallery-track]',
    media: '[data-hz-gallery-media]',
    mediaFrame: '[data-hz-gallery-media-frame]',
    mediaButton: '[data-hz-gallery-media-button]',
    expandButton: '[data-hz-gallery-expand-button]',
    videoToggle: '[data-hz-gallery-video-toggle]',
    loopClone: '[data-hz-gallery-loop-clone]',
    thumbnail: '[data-hz-gallery-thumbnail]',
    thumbRail: '[data-hz-gallery-thumb-rail]',
    thumbViewport: '[data-hz-gallery-thumb-viewport]',
    thumbList: '[data-hz-gallery-thumb-list]',
    thumbControls: '[data-hz-gallery-thumb-controls]',
    thumbPrevious: '[data-hz-gallery-thumb-previous]',
    thumbNext: '[data-hz-gallery-thumb-next]',
    indicators: '[data-hz-gallery-indicators]',
    indicator: '[data-hz-gallery-indicator]',
    status: '[data-hz-gallery-status]',
    arButton: '[data-hz-gallery-ar-button]',
    modal: '[data-hz-gallery-modal]',
    modalViewport: '[data-hz-gallery-modal-viewport]',
    modalStage: '[data-hz-gallery-modal-stage]',
    modalLoader: '[data-hz-gallery-modal-loader]',
    modalPrevious: '[data-hz-gallery-modal-previous]',
    modalNext: '[data-hz-gallery-modal-next]',
    modalCounter: '[data-hz-gallery-modal-counter]',
    modalThumbViewport: '[data-hz-gallery-modal-thumb-viewport]',
    modalThumbnail: '[data-hz-gallery-modal-thumbnail]',
    zoomControls: '[data-hz-gallery-zoom-controls]',
    zoomOut: '[data-hz-gallery-zoom-out]',
    zoomIn: '[data-hz-gallery-zoom-in]',
    zoomReset: '[data-hz-gallery-zoom-reset]',
    zoomValue: '[data-hz-gallery-zoom-value]',
    variantMap: '[data-hz-gallery-variant-map]'
  };

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
  const normalize = (value) => String(value ?? '').trim().toLowerCase().replaceAll('_', ' ').replace(/\s+/g, ' ');
  // Prestige can expose the hidden variant input named "id" through form.id.
  // Resolve either the expected string or that form-associated control safely.
  const resolveFormId = (value) => {
    if (typeof value === 'string') return value;
    if (value instanceof HTMLFormElement) return value.getAttribute('id') || '';
    if (value instanceof HTMLElement) {
      return value.form?.getAttribute('id')
        || value.getAttribute('form')
        || value.getAttribute('id')
        || '';
    }
    return '';
  };
  const normalizeMediaSource = (value) => {
    const source = String(value ?? '').trim();
    if (!source) return '';

    try {
      return decodeURIComponent(new URL(source, window.location.href).pathname);
    } catch (_error) {
      return source.split('?')[0];
    }
  };
  const toNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  class ArProductGallery extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.cleanupCallbacks = [];
      this.resizeObserver = null;
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.tabletThumbnailsQuery = window.matchMedia(TABLET_THUMBNAILS_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      this.connected = false;
      this.scrollFrame = 0;
      this.scrollSettleTimer = 0;
      this.lastMobileScrollAt = 0;
      this.overflowFrame = 0;
      this.mobileLoopRefreshFrame = 0;
      this.mobileLoopStartClone = null;
      this.mobileLoopEndClone = null;
      this.mobileLoopJumping = false;
      this.modalOpening = false;
      this.modalOpenToken = 0;
      this.modalAnimating = false;
      this.modalTransitionToken = 0;
      this.modalLoadToken = 0;
      this.modalLoadingStartedAt = 0;
      this.modalReadyMediaIds = new Set();
      this.modalPlacement = null;
      this.modalOpener = null;
      this.zoomScale = 1;
      this.panX = 0;
      this.panY = 0;
      this.pointers = new Map();
      this.pointerGesture = null;
      this.singlePointerStart = null;
      this.pinchStart = null;
      this.preloadIdleHandle = 0;
      this.preloadUsesIdleCallback = false;
      this.trackGestureMedia = null;
      this.trackGestureOpened = false;
      this.productForm = null;
      this.formAbortController = null;
      this.formObserver = null;
      this.formBindFrame = 0;
      this.breakpointFrame = 0;
      this.variantRerenderToken = 0;
      this.variantSyncFrame = 0;
      this.pendingVariantSync = null;
      this.variantMap = new Map();
      this.lastAppliedVariantSignature = '';
      this.lastAppliedVariantOptions = [];
    }

    connectedCallback() {
      if (this.connected) return;

      this.connected = true;
      this.abortController = new AbortController();
      this.cleanupCallbacks = [];
      this.cacheElements();

      if (!this.track || this.mediaItems.length === 0) return;

      this.activeMedia =
        this.mediaItems.find((media) => media.classList.contains('hz__productGalleryMedia--active') && !media.hidden) ||
        this.visibleMedia[0] ||
        this.mediaItems[0];

      this.bindEvents();
      this.observeLayout();
      this.setAttribute('data-hz-product-gallery-ready', '');
      this.syncResponsiveThumbnailOrientation();
      this.syncMediaDomOrder();
      if (this.isMobile) this.refreshMobileLoop({ position: false });
      this.syncSelection({ announce: false, settleMedia: false });

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.syncVerticalThumbnailRailHeight();
        this.updateThumbnailOverflow();
        this.syncMobilePosition({ behavior: 'auto' });
        this.syncIndicatorProgress({ forceActive: !this.isMobile });
        this.settleActiveMedia({ announce: false });
      });
    }

    disconnectedCallback() {
      this.connected = false;
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.formAbortController?.abort();
      this.formObserver?.disconnect();
      this.formObserver = null;
      this.cleanupCallbacks.forEach((callback) => callback());
      this.cleanupCallbacks = [];
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.overflowFrame);
      cancelAnimationFrame(this.mobileLoopRefreshFrame);
      cancelAnimationFrame(this.formBindFrame);
      cancelAnimationFrame(this.breakpointFrame);
      cancelAnimationFrame(this.variantSyncFrame);
      window.clearTimeout(this.scrollSettleTimer);
      this.cancelAdjacentPreload();
      this.modalTransitionToken += 1;
      this.modalLoadToken += 1;
      this.modalOpenToken += 1;
      this.modalOpening = false;
      this.modalAnimating = false;
      this.setModalLoading(false);
      this.restoreModalMedia();
      this.removeMobileLoopClones();

      if (this.modal?.hasAttribute('open')) {
        this.modal.removeAttribute('open');
        this.modal.style.setProperty('display', 'none');
      }

      if (this.modal?.parentElement === document.body) {
        this.modal.remove();
      }
    }

    cacheElements() {
      this.stage = this.querySelector(SELECTOR.stage);
      this.track = this.querySelector(SELECTOR.track);
      this.mediaItems = Array.from(this.querySelectorAll(SELECTOR.media));
      this.originalMediaOrder = [...this.mediaItems];
      this.thumbnails = Array.from(this.querySelectorAll(SELECTOR.thumbnail));
      this.indicators = Array.from(this.querySelectorAll(SELECTOR.indicator));
      this.videoToggles = Array.from(this.querySelectorAll(SELECTOR.videoToggle));
      this.thumbRail = this.querySelector(SELECTOR.thumbRail);
      this.thumbViewport = this.querySelector(SELECTOR.thumbViewport);
      this.thumbList = this.querySelector(SELECTOR.thumbList);
      this.thumbControls = this.querySelector(SELECTOR.thumbControls);
      this.thumbPrevious = this.querySelector(SELECTOR.thumbPrevious);
      this.thumbNext = this.querySelector(SELECTOR.thumbNext);
      this.indicatorContainer = this.querySelector(SELECTOR.indicators);
      this.status = this.querySelector(SELECTOR.status);
      this.arButton = this.querySelector(SELECTOR.arButton);
      this.modal = this.querySelector(SELECTOR.modal);
      this.modalViewport = this.modal?.querySelector(SELECTOR.modalViewport) ?? null;
      this.modalStage = this.modal?.querySelector(SELECTOR.modalStage) ?? null;
      this.modalLoader = this.modal?.querySelector(SELECTOR.modalLoader) ?? null;
      this.modalPrevious = this.modal?.querySelector(SELECTOR.modalPrevious) ?? null;
      this.modalNext = this.modal?.querySelector(SELECTOR.modalNext) ?? null;
      this.modalCounter = this.modal?.querySelector(SELECTOR.modalCounter) ?? null;
      this.modalThumbViewport = this.modal?.querySelector(SELECTOR.modalThumbViewport) ?? null;
      this.modalThumbnails = this.modal ? Array.from(this.modal.querySelectorAll(SELECTOR.modalThumbnail)) : [];
      this.zoomControls = this.modal?.querySelector(SELECTOR.zoomControls) ?? null;
      this.zoomOutButton = this.modal?.querySelector(SELECTOR.zoomOut) ?? null;
      this.zoomInButton = this.modal?.querySelector(SELECTOR.zoomIn) ?? null;
      this.zoomValue = this.modal?.querySelector(SELECTOR.zoomValue) ?? null;
      this.mediaById = new Map(this.mediaItems.map((media) => [String(media.dataset.hzMediaId), media]));
      this.mediaByPosition = new Map(this.mediaItems.map((media) => [String(media.dataset.hzMediaPosition || ''), media]));
      this.mediaBySource = new Map();
      this.mediaItems.forEach((media) => {
        const source = normalizeMediaSource(media.dataset.hzMediaSource);
        if (source && !this.mediaBySource.has(source)) this.mediaBySource.set(source, media);
      });
      this.variantMap = this.readVariantMap();
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => this.onGalleryClick(event), { signal });
      this.addEventListener('keydown', (event) => this.onGalleryKeydown(event), { signal });
      this.track.addEventListener('scroll', () => this.onTrackScroll(), { passive: true, signal });
      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', () => {
          this.settleMobileLoopPosition();
          this.settleActiveMedia({ announce: true });
          this.syncIndicatorProgress();
        }, { signal });
      }
      this.track.addEventListener('gesturestart', (event) => this.onTrackGestureStart(event), {
        passive: false,
        signal
      });
      this.track.addEventListener('gesturechange', (event) => this.onTrackGestureChange(event), {
        passive: false,
        signal
      });
      this.track.addEventListener('gestureend', () => this.onTrackGestureEnd(), { signal });
      this.thumbViewport?.addEventListener('scroll', () => this.updateThumbnailControlState(), {
        passive: true,
        signal
      });
      this.bindProductFormEvents(signal);
      document.addEventListener('variant:change', (event) => this.onVariantChange(event), { signal });
      document.addEventListener('change', (event) => this.onProductFormControlChange(event), { signal, capture: true });
      document.addEventListener('media:play', (event) => this.onNativeMediaStateEvent(event, true), { signal });
      document.addEventListener('media:pause', (event) => this.onNativeMediaStateEvent(event, false), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.mediaItems.forEach((media) => this.pauseMedia(media));
      }, { signal });

      this.addMediaQueryListener(this.mobileQuery, () => this.scheduleBreakpointChange());
      this.addMediaQueryListener(this.tabletThumbnailsQuery, () => this.scheduleBreakpointChange());

      this.modal?.addEventListener('click', (event) => this.onModalClick(event), { signal });
      this.modal?.addEventListener('keydown', (event) => this.onModalKeydown(event), { signal });
      this.modal?.addEventListener('dialog:before-hide', () => this.onModalBeforeHide(), { signal });
      this.modal?.addEventListener('dialog:after-hide', () => this.onModalAfterHide(), { signal });
      this.modal?.addEventListener('dialog:after-show', () => this.onModalAfterShow(), { signal });

      this.modalViewport?.addEventListener('wheel', (event) => this.onModalWheel(event), {
        passive: false,
        signal
      });
      this.modalViewport?.addEventListener('dblclick', (event) => this.onModalDoubleClick(event), { signal });
      this.modalViewport?.addEventListener('pointerdown', (event) => this.onModalPointerDown(event), { signal });
      this.modalViewport?.addEventListener('pointermove', (event) => this.onModalPointerMove(event), { signal });
      this.modalViewport?.addEventListener('pointerup', (event) => this.onModalPointerEnd(event), { signal });
      this.modalViewport?.addEventListener('pointercancel', (event) => this.onModalPointerEnd(event), { signal });

      this.mediaItems.forEach((media) => {
        media.querySelectorAll('img').forEach((image) => {
          if (image.complete) return;
          image.addEventListener('load', () => {
            this.scheduleThumbnailOverflowUpdate();
            this.syncIndicatorProgress({ forceActive: !this.isMobile });
          }, { once: true, signal });
        });
      });
    }

    onTrackGestureStart(event) {
      if (!this.isMobile || this.modal?.hasAttribute('open')) return;
      const media = event.target instanceof Element ? event.target.closest(SELECTOR.media) : null;
      if (!media || !this.mediaItems.includes(media) || media.dataset.hzMediaType !== 'image') return;
      event.preventDefault();
      this.trackGestureMedia = media;
      this.trackGestureOpened = false;
    }

    onTrackGestureChange(event) {
      if (!this.trackGestureMedia || this.trackGestureOpened || !this.isMobile) return;
      event.preventDefault();
      if (toNumber(event.scale, 1) < 1.45) return;
      this.trackGestureOpened = true;
      const opener = this.trackGestureMedia.querySelector(SELECTOR.mediaButton);
      void this.openModal(this.trackGestureMedia, opener);
    }

    onTrackGestureEnd() {
      this.trackGestureMedia = null;
      this.trackGestureOpened = false;
    }

    bindProductFormEvents(signal) {
      const formId = this.dataset.hzProductFormId;
      if (!formId) return;

      const bind = () => {
        this.formBindFrame = 0;
        if (!this.isConnected || signal.aborted) return;

        const form = document.forms.namedItem(formId) || document.getElementById(formId);
        if (!(form instanceof HTMLFormElement)) {
          this.formBindFrame = requestAnimationFrame(bind);
          return;
        }

        if (form === this.productForm && !this.formAbortController?.signal.aborted) return;

        this.formAbortController?.abort();
        this.formAbortController = new AbortController();
        const formSignal = this.formAbortController.signal;
        this.productForm = form;

        form.addEventListener('variant:change', (event) => this.onVariantChange(event), { signal: formSignal });
        form.addEventListener('product:rerender', (event) => this.onProductRerender(event), { signal: formSignal });
        form.addEventListener('change', (event) => this.onProductFormControlChange(event), { signal: formSignal });

        this.applyVariantFromFormValue({ source: 'form-bind' });
      };

      bind();

      if ('MutationObserver' in window && !this.formObserver) {
        const section = this.closest('[data-hz-main-product-section]');
        const scope = section?.querySelector('[data-hz-product-column="content"]') || section || document.body;
        this.formObserver = new MutationObserver(() => {
          const currentForm = document.forms.namedItem(formId) || document.getElementById(formId);
          if (currentForm !== this.productForm) bind();
        });
        this.formObserver.observe(scope, { childList: true, subtree: true });
      }
    }

    addMediaQueryListener(mediaQuery, callback) {
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', callback, { signal: this.abortController.signal });
      } else {
        mediaQuery.addListener(callback);
        this.cleanupCallbacks.push(() => mediaQuery.removeListener(callback));
      }
    }

    observeLayout() {
      const onLayoutChange = () => {
        this.syncVerticalThumbnailRailHeight();
        this.scheduleThumbnailOverflowUpdate();
        this.syncIndicatorProgress({ forceActive: !this.isMobile });
        if (this.modal?.hasAttribute('open')) {
          this.clampPan();
          this.applyZoomStyles();
        }
      };

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(onLayoutChange);
        [this, this.stage, this.track, this.thumbRail, this.thumbViewport, this.thumbList, this.modalViewport]
          .filter(Boolean)
          .forEach((element) => this.resizeObserver.observe(element));
      } else {
        window.addEventListener('resize', onLayoutChange, { passive: true, signal: this.abortController.signal });
      }
    }

    get isMobile() {
      return this.mobileQuery.matches;
    }

    get isTabletThumbnailMode() {
      return this.tabletThumbnailsQuery.matches;
    }

    get usesVerticalThumbnails() {
      return this.isTabletThumbnailMode || (!this.isMobile && this.desktopLayout === 'thumbnails_left');
    }

    get usesThumbnails() {
      return this.isTabletThumbnailMode || (!this.isMobile && this.desktopLayout.startsWith('thumbnails_'));
    }

    get reducedMotion() {
      return this.reducedMotionQuery.matches;
    }

    get desktopLayout() {
      return this.dataset.hzDesktopLayout || 'highlighted_grid';
    }

    get highlightedSecondaryAction() {
      return this.dataset.hzHighlightedSecondaryAction === 'gallery' ? 'gallery' : 'promote';
    }

    get stackImageAction() {
      return this.dataset.hzStackImageAction === 'gallery' ? 'gallery' : 'none';
    }

    get showOnlySelectedVariantMedia() {
      return this.dataset.hzShowOnlySelectedVariantMedia === 'true';
    }

    get visibleMedia() {
      return this.mediaItems.filter((media) => !media.hidden);
    }

    get visibleThumbnails() {
      return this.thumbnails.filter((thumbnail) => !thumbnail.hidden);
    }

    get mobileScrollItems() {
      if (!this.track) return [];
      return Array.from(this.track.children).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.matches(SELECTOR.loopClone)) return true;
        return element.matches(SELECTOR.media) && !element.hidden;
      });
    }

    get maxZoom() {
      return clamp(toNumber(this.dataset.hzMaxZoom, 3), 1, 8);
    }

    get modalImageSizes() {
      return `${Math.round(this.maxZoom * 100)}vw`;
    }

    onGalleryClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const videoToggle = target.closest(SELECTOR.videoToggle);
      if (videoToggle && (this.contains(videoToggle) || this.modal?.contains(videoToggle))) {
        event.preventDefault();
        event.stopPropagation();
        const media = videoToggle.closest(SELECTOR.media);
        if (media) this.toggleVideo(media);
        return;
      }

      const thumbnail = target.closest(SELECTOR.thumbnail);
      if (thumbnail && this.contains(thumbnail)) {
        event.preventDefault();
        const media = this.mediaById.get(String(thumbnail.dataset.hzMediaId));
        if (media) this.selectMedia(media, { source: 'thumbnail', announce: true });
        return;
      }

      const indicator = target.closest(SELECTOR.indicator);
      if (indicator && this.contains(indicator)) {
        event.preventDefault();
        const media = this.mediaItems.find(
          (item) => String(item.dataset.hzMediaIndex) === String(indicator.dataset.hzMediaIndex)
        );
        if (media) this.selectMedia(media, { source: 'indicator', announce: true });
        return;
      }

      if (target.closest(SELECTOR.thumbPrevious)) {
        event.preventDefault();
        this.pageThumbnails(-1);
        return;
      }

      if (target.closest(SELECTOR.thumbNext)) {
        event.preventDefault();
        this.pageThumbnails(1);
        return;
      }

      const expandButton = target.closest(SELECTOR.expandButton);
      if (expandButton && (this.contains(expandButton) || this.modal?.contains(expandButton))) {
        event.preventDefault();
        if (this.isMobile && performance.now() - this.lastMobileScrollAt < 110) return;
        const media = expandButton.closest(SELECTOR.media);
        if (media) this.executeMediaOperation(media, 'gallery', expandButton);
        return;
      }

      const mediaButton = target.closest(SELECTOR.mediaButton);
      if (!mediaButton || (!this.contains(mediaButton) && !this.modal?.contains(mediaButton))) return;

      event.preventDefault();
      if (this.isMobile && performance.now() - this.lastMobileScrollAt < 110) return;
      const media = mediaButton.closest(SELECTOR.media);
      if (!media || media.hidden) return;

      this.executeMediaOperation(media, mediaButton.dataset.hzGalleryOperation || 'gallery', mediaButton);
    }

    onGalleryKeydown(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const thumbnail = target.closest(SELECTOR.thumbnail);
      const indicator = target.closest(SELECTOR.indicator);
      const control = thumbnail || indicator;
      if (!control) return;

      const isHorizontal = !thumbnail || !this.usesVerticalThumbnails;
      const isRtl = document.documentElement.dir === 'rtl';
      const previousKeys = isHorizontal
        ? [isRtl ? 'ArrowRight' : 'ArrowLeft', 'ArrowUp']
        : ['ArrowUp', isRtl ? 'ArrowRight' : 'ArrowLeft'];
      const nextKeys = isHorizontal
        ? [isRtl ? 'ArrowLeft' : 'ArrowRight', 'ArrowDown']
        : ['ArrowDown', isRtl ? 'ArrowLeft' : 'ArrowRight'];
      if (![...previousKeys, ...nextKeys, 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const controls = (thumbnail ? this.visibleThumbnails : this.indicators.filter((item) => !item.hidden));
      const currentIndex = controls.indexOf(control);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (previousKeys.includes(event.key)) nextIndex = Math.max(0, currentIndex - 1);
      if (nextKeys.includes(event.key)) nextIndex = Math.min(controls.length - 1, currentIndex + 1);
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = controls.length - 1;

      controls[nextIndex]?.focus({ preventScroll: true });
      controls[nextIndex]?.click();
    }

    onTrackScroll() {
      if (!this.isMobile || this.modal?.hasAttribute('open') || this.mobileLoopJumping) return;

      this.lastMobileScrollAt = performance.now();
      this.dataset.hzGalleryScrolling = 'true';

      if (!this.scrollFrame) {
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.syncActiveMediaFromMobileScroll();
          this.syncIndicatorProgress();
        });
      }

      window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = window.setTimeout(() => {
        this.settleMobileLoopPosition();
        this.settleActiveMedia({ announce: true });
        this.syncIndicatorProgress();
        window.setTimeout(() => {
          if (this.isConnected) delete this.dataset.hzGalleryScrolling;
        }, this.reducedMotion ? 0 : 110);
      }, 140);
    }

    syncActiveMediaFromMobileScroll() {
      const candidateItem = this.findClosestMobileScrollItem();
      const candidate = this.getLogicalMediaForScrollItem(candidateItem);
      if (!candidate || candidate.hidden) return;

      if (candidate !== this.activeMedia) {
        this.setActiveMedia(candidate, { animate: false, announce: false, settleMedia: false });
      }
    }

    getLogicalMediaForScrollItem(item) {
      if (!item) return null;
      if (item.matches(SELECTOR.loopClone)) {
        return this.mediaById.get(String(item.dataset.hzLoopMediaId)) || null;
      }
      return item.matches(SELECTOR.media) ? item : null;
    }

    findClosestMobileScrollItem() {
      const items = this.mobileScrollItems;
      if (items.length === 0 || !this.track) return null;

      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let candidate = items[0];
      let smallestDistance = Number.POSITIVE_INFINITY;

      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
        if (distance < smallestDistance) {
          candidate = item;
          smallestDistance = distance;
        }
      });

      return candidate;
    }

    createMobileLoopClone(sourceMedia, position) {
      if (!sourceMedia) return null;

      const clone = document.createElement('div');
      clone.className = 'hz__productGalleryMedia hz__productGalleryMedia--loopClone';
      clone.dataset.hzGalleryLoopClone = position;
      clone.dataset.hzLoopMediaId = String(sourceMedia.dataset.hzMediaId || '');
      clone.dataset.hzMediaType = sourceMedia.dataset.hzMediaType || 'image';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');

      const frame = document.createElement('div');
      frame.className = 'hz__productGalleryMediaFrame';
      const sourceImage = sourceMedia.querySelector(`${SELECTOR.mediaFrame} img`) || this.thumbnails
        .find((thumbnail) => String(thumbnail.dataset.hzMediaId) === String(sourceMedia.dataset.hzMediaId))
        ?.querySelector('img');

      if (sourceImage) {
        const image = sourceImage.cloneNode(false);
        image.removeAttribute('id');
        image.removeAttribute('aria-describedby');
        image.setAttribute('alt', '');
        image.setAttribute('aria-hidden', 'true');
        image.setAttribute('draggable', 'false');
        image.setAttribute('loading', 'eager');
        image.setAttribute('decoding', 'async');
        image.setAttribute('sizes', '100vw');
        image.classList.add('hz__productGalleryLoopCloneImage');
        frame.append(image);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'hz__productGalleryLoopCloneFallback';
        fallback.setAttribute('aria-hidden', 'true');
        frame.append(fallback);
      }

      clone.append(frame);
      const badge = sourceMedia.querySelector('.hz__productGalleryMediaBadge');
      if (badge) clone.append(badge.cloneNode(true));
      return clone;
    }

    removeMobileLoopClones() {
      this.mobileLoopStartClone?.remove();
      this.mobileLoopEndClone?.remove();
      this.track?.querySelectorAll(SELECTOR.loopClone).forEach((clone) => clone.remove());
      this.mobileLoopStartClone = null;
      this.mobileLoopEndClone = null;
    }

    restoreCanonicalMediaOrder() {
      if (!this.track) return;
      this.originalMediaOrder
        .filter((media) => media.parentElement === this.track)
        .forEach((media) => this.track.append(media));
    }

    buildMobileLoopClones() {
      if (!this.isMobile || !this.track || this.modal?.hasAttribute('open')) return;
      const mediaInTrack = this.visibleMedia.filter((media) => media.parentElement === this.track);
      if (mediaInTrack.length <= 1) return;

      this.mobileLoopStartClone = this.createMobileLoopClone(mediaInTrack[mediaInTrack.length - 1], 'start');
      this.mobileLoopEndClone = this.createMobileLoopClone(mediaInTrack[0], 'end');
      if (this.mobileLoopStartClone) this.track.prepend(this.mobileLoopStartClone);
      if (this.mobileLoopEndClone) this.track.append(this.mobileLoopEndClone);
    }

    refreshMobileLoop({ position = true } = {}) {
      this.removeMobileLoopClones();
      this.restoreCanonicalMediaOrder();
      if (!this.isMobile) return;
      this.buildMobileLoopClones();

      if (position) {
        cancelAnimationFrame(this.mobileLoopRefreshFrame);
        this.mobileLoopRefreshFrame = requestAnimationFrame(() => {
          this.mobileLoopRefreshFrame = 0;
          if (!this.isConnected || this.modal?.hasAttribute('open')) return;
          this.syncMobilePosition({ behavior: 'auto' });
          this.syncIndicatorProgress();
        });
      }
    }

    syncIndicatorProgress({ forceActive = false } = {}) {
      const visibleMedia = this.visibleMedia;
      if (visibleMedia.length === 0) return;

      const progressByMedia = new Map(visibleMedia.map((media) => [media, 0]));
      const useGeometry = this.isMobile && !forceActive && this.track?.clientWidth > 0;

      if (useGeometry) {
        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        const denominator = Math.max(1, trackRect.width);

        this.mobileScrollItems.forEach((item) => {
          const media = this.getLogicalMediaForScrollItem(item);
          if (!media || media.hidden) return;
          const rect = item.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
          const linearProgress = clamp(1 - distance / denominator, 0, 1);
          const smoothProgress = linearProgress * linearProgress * (3 - 2 * linearProgress);
          progressByMedia.set(media, Math.max(progressByMedia.get(media) || 0, smoothProgress));
        });
      } else {
        progressByMedia.set(this.activeMedia, 1);
      }

      this.indicators.forEach((indicator) => {
        const media = this.mediaItems.find(
          (item) => String(item.dataset.hzMediaIndex) === String(indicator.dataset.hzMediaIndex)
        );
        const progress = media ? progressByMedia.get(media) || 0 : 0;
        indicator.style.setProperty('--hz-product-gallery-indicator-progress', progress.toFixed(4));
      });
    }

    settleMobileLoopPosition() {
      if (!this.isMobile || !this.mobileLoopStartClone || !this.mobileLoopEndClone) return false;
      const closest = this.findClosestMobileScrollItem();
      if (!closest?.matches(SELECTOR.loopClone)) return false;

      const media = this.getLogicalMediaForScrollItem(closest);
      if (!media) return false;
      if (media !== this.activeMedia) {
        this.setActiveMedia(media, { animate: false, announce: false, settleMedia: false });
      }
      this.jumpToMobileMedia(media);
      return true;
    }

    jumpToMobileMedia(media) {
      if (!this.track || !media || media.parentElement !== this.track) return;
      this.mobileLoopJumping = true;
      const previousScrollBehavior = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      media.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });

      requestAnimationFrame(() => {
        if (previousScrollBehavior) {
          this.track.style.scrollBehavior = previousScrollBehavior;
        } else {
          this.track.style.removeProperty('scroll-behavior');
        }
        this.mobileLoopJumping = false;
        this.syncIndicatorProgress();
      });
    }

    scheduleBreakpointChange() {
      cancelAnimationFrame(this.breakpointFrame);
      this.breakpointFrame = requestAnimationFrame(() => {
        this.breakpointFrame = 0;
        void this.onBreakpointChange();
      });
    }

    async onBreakpointChange() {
      if (this.modal?.hasAttribute('open')) {
        await this.modal.hide?.();
      }
      if (!this.isConnected) return;

      this.removeMobileLoopClones();
      this.restoreCanonicalMediaOrder();
      this.syncResponsiveThumbnailOrientation();
      this.syncMediaDomOrder();
      if (this.isMobile) this.refreshMobileLoop({ position: false });
      this.syncSelection({ announce: false, settleMedia: true });
      this.scheduleThumbnailOverflowUpdate();
      requestAnimationFrame(() => {
        this.syncMobilePosition({ behavior: 'auto' });
        this.syncIndicatorProgress({ forceActive: !this.isMobile });
      });
    }

    readVariantMap() {
      const script = this.querySelector(SELECTOR.variantMap);
      if (!script?.textContent) return new Map();

      try {
        const variants = JSON.parse(script.textContent);
        if (!Array.isArray(variants)) return new Map();
        return new Map(
          variants
            .filter((variant) => variant?.id != null)
            .map((variant) => [String(variant.id), variant])
        );
      } catch (_error) {
        return new Map();
      }
    }

    getVariantPicker() {
      const formId = this.dataset.hzProductFormId;
      if (!formId) return null;
      return document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    }

    getNativeSelectedVariant() {
      const picker = this.getVariantPicker();
      if (!picker) return null;
      if (picker.selectedVariant?.id != null) return picker.selectedVariant;

      const variantScript = picker.querySelector('script[data-variant]');
      if (!variantScript?.textContent) return null;
      try {
        const variant = JSON.parse(variantScript.textContent);
        return variant?.id != null ? variant : null;
      } catch (_error) {
        return null;
      }
    }

    getSelectedOptionNames() {
      if (!this.productForm) return [];

      const selectedInputs = Array.from(this.productForm.elements)
        .filter((element) => element instanceof HTMLInputElement)
        .filter((input) => input.matches('input[data-option-position]:checked'))
        .sort((a, b) => toNumber(a.dataset.optionPosition) - toNumber(b.dataset.optionPosition));
      if (selectedInputs.length === 0) return [];

      const values = [];
      selectedInputs.forEach((input) => {
        const position = Math.max(1, Math.trunc(toNumber(input.dataset.optionPosition, 1)));
        const label = input.dataset.hzOptionValueName
          || input.labels?.[0]?.dataset.hzOptionValueName
          || input.labels?.[0]?.textContent
          || '';
        values[position - 1] = String(label).trim();
      });
      return values;
    }

    getVariantFromSelectedOptions() {
      const selectedOptions = this.getSelectedOptionNames();
      if (selectedOptions.length === 0 || selectedOptions.some((value) => !value)) return null;

      return Array.from(this.variantMap.values()).find((variant) => {
        if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) return false;
        return selectedOptions.every((value, index) => normalize(variant.options[index]) === normalize(value));
      }) || null;
    }

    getVariantFromFormValue() {
      if (!this.productForm) return null;
      const variantControl = this.productForm.elements.namedItem('id');
      const variantId = variantControl && 'value' in variantControl ? String(variantControl.value || '') : '';
      return variantId ? this.variantMap.get(variantId) || null : null;
    }

    requestVariantSync(variant, { source = 'variant', preferredOptionPositions = [] } = {}) {
      if (!variant) return;
      this.pendingVariantSync = {
        variant,
        source,
        preferredOptionPositions: Array.from(new Set(preferredOptionPositions.map((position) => Number(position)).filter((position) => position > 0)))
      };
      cancelAnimationFrame(this.variantSyncFrame);
      this.variantSyncFrame = requestAnimationFrame(() => {
        this.variantSyncFrame = 0;
        const pending = this.pendingVariantSync;
        this.pendingVariantSync = null;
        if (!this.isConnected || !pending) return;
        this.applyVariant(pending.variant, pending);
      });
    }

    applyVariantFromFormValue({ source = 'form' } = {}) {
      const selectedVariant = this.getNativeSelectedVariant()
        || this.getVariantFromFormValue()
        || this.getVariantFromSelectedOptions();
      if (selectedVariant) this.requestVariantSync(selectedVariant, { source });
    }

    onProductFormControlChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

      const associatedFormId = target.form?.getAttribute('id') || target.getAttribute('form') || '';
      if (associatedFormId !== this.dataset.hzProductFormId) return;

      if (target.matches('input[data-option-position]')) {
        const selectedVariant = this.getVariantFromSelectedOptions();
        if (selectedVariant) {
          this.requestVariantSync(selectedVariant, {
            source: 'option-control',
            preferredOptionPositions: [target.dataset.optionPosition]
          });
        }
        return;
      }

      if (target.matches('[name="id"]')) {
        const selectedVariant = this.getNativeSelectedVariant()
          || this.getVariantFromFormValue()
          || this.getVariantFromSelectedOptions();
        if (selectedVariant) this.requestVariantSync(selectedVariant, { source: 'variant-id' });
      }
    }

    enrichVariant(variant) {
      if (!variant) return null;
      const variantId = variant.id != null ? String(variant.id) : '';
      const mapped = variantId ? this.variantMap.get(variantId) : null;
      if (!mapped) return variant;

      return {
        ...mapped,
        ...variant,
        options: Array.isArray(variant.options) && variant.options.length > 0 ? variant.options : mapped.options,
        featured_media: variant.featured_media || (mapped.featuredMediaId != null || mapped.featuredMediaSource ? {
          id: mapped.featuredMediaId,
          position: mapped.featuredMediaPosition,
          preview_image: mapped.featuredMediaSource ? { src: mapped.featuredMediaSource } : null
        } : null),
        featured_image: variant.featured_image || (mapped.featuredImageId != null || mapped.featuredImageSrc ? {
          id: mapped.featuredImageId,
          src: mapped.featuredImageSrc
        } : null)
      };
    }

    resolveFeaturedMedia(variant) {
      const featuredMediaId = variant?.featured_media?.id
        ?? variant?.featuredMediaId
        ?? variant?.featured_image?.id
        ?? variant?.featuredImageId;
      if (featuredMediaId != null) {
        const byId = this.mediaById.get(String(featuredMediaId));
        if (byId) return byId;
      }

      const featuredPosition = variant?.featured_media?.position ?? variant?.featuredMediaPosition ?? variant?.featured_image?.position;
      if (featuredPosition != null) {
        const byPosition = this.mediaByPosition.get(String(featuredPosition));
        if (byPosition) return byPosition;
      }

      const featuredSource = normalizeMediaSource(
        variant?.featured_media?.preview_image?.src
          ?? variant?.featuredMediaSource
          ?? variant?.featured_image?.src
          ?? variant?.featuredImageSrc
      );
      if (featuredSource) {
        const bySource = this.mediaBySource.get(featuredSource);
        if (bySource) return bySource;
      }

      return null;
    }

    onProductRerender(event) {
      if (event.detail?.productChange) return;

      const fragment = event.detail?.htmlFragment;
      if (!(fragment instanceof DocumentFragment || fragment instanceof Element)) return;

      const formId = this.dataset.hzProductFormId;
      const picker = Array.from(fragment.querySelectorAll('variant-picker[form-id]')).find(
        (element) => element.getAttribute('form-id') === formId
      );
      const variantScript = picker?.querySelector('script[data-variant]');
      if (!variantScript?.textContent) return;

      let variant;
      try {
        variant = JSON.parse(variantScript.textContent);
      } catch (_error) {
        return;
      }
      if (!variant?.id) return;

      const token = ++this.variantRerenderToken;
      requestAnimationFrame(() => {
        if (!this.isConnected || token !== this.variantRerenderToken) return;
        this.requestVariantSync(variant, { source: 'rerender' });
      });
    }

    onVariantChange(event) {
      const expectedFormId = this.dataset.hzProductFormId;
      const eventFormId = resolveFormId(event.detail?.formId)
        || resolveFormId(event.target)
        || resolveFormId(event.currentTarget);
      if (expectedFormId && expectedFormId !== eventFormId) return;

      const variant = event.detail?.variant;
      if (!variant) return;
      this.variantRerenderToken += 1;

      const previousOptions = Array.isArray(event.detail?.previousVariant?.options)
        ? event.detail.previousVariant.options
        : this.lastAppliedVariantOptions;
      const nextOptions = Array.isArray(variant.options) ? variant.options : [];
      const preferredOptionPositions = nextOptions
        .map((value, index) => normalize(value) !== normalize(previousOptions[index]) ? index + 1 : 0)
        .filter((position) => position > 0);

      this.requestVariantSync(variant, { source: 'variant', preferredOptionPositions });
    }

    applyVariant(variant, { source = 'variant', preferredOptionPositions = [] } = {}) {
      const enrichedVariant = this.enrichVariant(variant);
      if (!enrichedVariant) return;

      const variantId = enrichedVariant.id != null ? String(enrichedVariant.id) : '';
      const selectedOptions = Array.isArray(enrichedVariant.options) && enrichedVariant.options.length > 0
        ? enrichedVariant.options
        : this.getSelectedOptionNames();
      const featuredMedia = this.resolveFeaturedMedia(enrichedVariant);
      const featuredMediaId = featuredMedia ? String(featuredMedia.dataset.hzMediaId) : '';
      const signature = `${variantId}|${featuredMediaId}|${selectedOptions.map(normalize).join('|')}`;

      if (signature && signature === this.lastAppliedVariantSignature && source !== 'force') return;

      const changedOptionPositions = new Set(
        preferredOptionPositions.map((position) => Number(position)).filter((position) => position > 0)
      );
      selectedOptions.forEach((value, index) => {
        if (normalize(value) !== normalize(this.lastAppliedVariantOptions[index])) {
          changedOptionPositions.add(index + 1);
        }
      });

      this.lastAppliedVariantSignature = signature;
      this.lastAppliedVariantOptions = [...selectedOptions];
      if (variantId) {
        this.dataset.hzCurrentVariantId = variantId;
        this.dataset.hzActiveVariantId = variantId;
      }

      if (this.isMobile) this.removeMobileLoopClones();

      this.mediaItems.forEach((media) => {
        const optionPosition = Number.parseInt(media.dataset.hzMediaOptionPosition || '0', 10);
        const mediaOptionValue = media.dataset.hzMediaOptionValue || '';
        const selectedValue = optionPosition > 0 ? selectedOptions[optionPosition - 1] : '';
        const belongsToAnotherOption =
          this.showOnlySelectedVariantMedia &&
          optionPosition > 0 &&
          mediaOptionValue !== '' &&
          selectedValue !== '' &&
          normalize(selectedValue) !== normalize(mediaOptionValue);
        const shouldKeepFeaturedMedia = featuredMedia === media;

        this.setMediaVisibility(media, !belongsToAnotherOption || shouldKeepFeaturedMedia);
      });

      const matchingMediaCandidates = this.mediaItems.filter((media) => {
        if (media.hidden) return false;
        const optionPosition = Number.parseInt(media.dataset.hzMediaOptionPosition || '0', 10);
        const mediaOptionValue = media.dataset.hzMediaOptionValue || '';
        const selectedValue = optionPosition > 0 ? selectedOptions[optionPosition - 1] : '';
        return (
          optionPosition > 0 &&
          mediaOptionValue !== '' &&
          selectedValue !== '' &&
          normalize(selectedValue) === normalize(mediaOptionValue)
        );
      });
      matchingMediaCandidates.sort((mediaA, mediaB) => {
        const positionA = Number.parseInt(mediaA.dataset.hzMediaOptionPosition || '0', 10);
        const positionB = Number.parseInt(mediaB.dataset.hzMediaOptionPosition || '0', 10);
        const preferredA = changedOptionPositions.has(positionA) ? 0 : 1;
        const preferredB = changedOptionPositions.has(positionB) ? 0 : 1;
        if (preferredA !== preferredB) return preferredA - preferredB;
        return Number.parseInt(mediaA.dataset.hzMediaPosition || '0', 10)
          - Number.parseInt(mediaB.dataset.hzMediaPosition || '0', 10);
      });
      const matchingMedia = matchingMediaCandidates[0] || null;

      if (this.visibleMedia.length === 0) {
        const fallbackMedia = featuredMedia || matchingMedia || this.mediaItems[0];
        if (fallbackMedia) this.setMediaVisibility(fallbackMedia, true);
      }

      const targetMedia = featuredMedia && !featuredMedia.hidden
        ? featuredMedia
        : matchingMedia
          ? matchingMedia
          : !this.activeMedia?.hidden
            ? this.activeMedia
            : this.visibleMedia[0];

      if (targetMedia) {
        this.dataset.hzActiveMediaId = String(targetMedia.dataset.hzMediaId || '');
        if (this.modal?.hasAttribute('open')) {
          this.setActiveMedia(targetMedia, { animate: false, announce: true, settleMedia: false });
          if (targetMedia === this.modalPlacement?.media) {
            this.syncModalControls();
            this.syncVideoControls();
          } else {
            void this.showModalMedia(targetMedia);
          }
        } else if (this.isMobile) {
          this.setActiveMedia(targetMedia, { animate: false, announce: true, settleMedia: false });
          this.refreshMobileLoop({ position: false });
          requestAnimationFrame(() => {
            if (!this.isConnected || targetMedia.hidden) return;
            this.scrollMediaIntoView(targetMedia, this.reducedMotion ? 'auto' : 'smooth');
            this.syncIndicatorProgress();
            this.settleActiveMedia({ announce: false });
          });
        } else {
          this.selectMedia(targetMedia, { source: 'variant', announce: true });
        }
      } else {
        if (this.isMobile) this.refreshMobileLoop({ position: false });
        this.syncSelection({ announce: false, settleMedia: false });
      }

      this.scheduleThumbnailOverflowUpdate();
    }

    setMediaVisibility(media, visible) {
      media.hidden = !visible;
      const mediaId = String(media.dataset.hzMediaId);
      this.thumbnails
        .filter((thumbnail) => String(thumbnail.dataset.hzMediaId) === mediaId)
        .forEach((thumbnail) => {
          thumbnail.hidden = !visible;
        });
      this.indicators
        .filter((indicator) => String(indicator.dataset.hzMediaIndex) === String(media.dataset.hzMediaIndex))
        .forEach((indicator) => {
          indicator.hidden = !visible;
        });
      this.modalThumbnails
        .filter((thumbnail) => String(thumbnail.dataset.hzMediaId) === mediaId)
        .forEach((thumbnail) => {
          thumbnail.hidden = !visible;
        });
    }

    selectMedia(media, { source = 'programmatic', announce = true } = {}) {
      if (!media || media.hidden) return;

      const animate = !this.reducedMotion && !this.isMobile && source !== 'initial';
      this.setActiveMedia(media, { animate, announce, settleMedia: !this.isMobile });

      if (this.isMobile) {
        this.scrollMediaIntoView(media, source === 'initial' ? 'auto' : 'smooth');
      } else if (this.desktopLayout === 'stack' && source === 'variant') {
        media.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }

    setActiveMedia(media, { animate = true, announce = true, settleMedia = true } = {}) {
      if (!media || media.hidden) return;

      const previousMedia = this.activeMedia;
      const shouldFlip =
        animate &&
        previousMedia !== media &&
        !this.isMobile &&
        this.desktopLayout === 'highlighted_grid';
      const beforeRects = shouldFlip ? this.captureVisibleMediaRects() : null;
      const shouldAnimateHeight =
        animate &&
        previousMedia !== media &&
        !this.isMobile &&
        this.desktopLayout.startsWith('thumbnails_') &&
        this.dataset.hzDesktopMediaSizing === 'adapt';
      const previousTrackHeight = shouldAnimateHeight ? this.track.getBoundingClientRect().height : 0;

      if (shouldAnimateHeight) this.track.style.height = `${previousTrackHeight}px`;

      this.mediaItems.forEach((item) => {
        item.classList.toggle('hz__productGalleryMedia--active', item === media);
      });
      this.activeMedia = media;
      this.syncMediaDomOrder();
      this.syncSelection({ announce, settleMedia });

      if (shouldFlip && beforeRects) this.animateHighlightedGrid(beforeRects);
      if (shouldAnimateHeight) this.animateAdaptiveTrackHeight(previousTrackHeight);
    }

    syncMediaDomOrder() {
      if (!this.track || this.modalPlacement || this.isMobile) return;
      this.removeMobileLoopClones();

      const canonicalOrder = this.originalMediaOrder.filter((media) => media.parentElement === this.track);
      const shouldLeadWithActive =
        this.desktopLayout === 'highlighted_grid' &&
        this.activeMedia?.parentElement === this.track;
      const desiredOrder = shouldLeadWithActive
        ? [this.activeMedia, ...canonicalOrder.filter((media) => media !== this.activeMedia)]
        : canonicalOrder;

      let referenceNode = this.track.firstElementChild;
      desiredOrder.forEach((media) => {
        if (media === referenceNode) {
          referenceNode = referenceNode.nextElementSibling;
          return;
        }

        if (typeof this.track.moveBefore === 'function') {
          this.track.moveBefore(media, referenceNode);
        } else {
          this.track.insertBefore(media, referenceNode);
        }
      });
    }

    captureVisibleMediaRects() {
      return new Map(
        this.visibleMedia.map((media) => [media, media.getBoundingClientRect()])
      );
    }

    animateHighlightedGrid(beforeRects) {
      const duration = 440;
      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

      this.visibleMedia.forEach((media) => {
        const before = beforeRects.get(media);
        if (!before) return;

        const after = media.getBoundingClientRect();
        if (!after.width || !after.height) return;

        const deltaX = before.left - after.left;
        const deltaY = before.top - after.top;
        const scaleX = before.width / after.width;
        const scaleY = before.height / after.height;
        const changed =
          Math.abs(deltaX) > 0.5 ||
          Math.abs(deltaY) > 0.5 ||
          Math.abs(scaleX - 1) > 0.005 ||
          Math.abs(scaleY - 1) > 0.005;
        if (!changed) return;

        media.getAnimations().forEach((animation) => animation.cancel());
        media.animate(
          [
            {
              transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${scaleX}, ${scaleY})`,
              transformOrigin: 'top left'
            },
            { transform: 'translate3d(0, 0, 0) scale(1, 1)', transformOrigin: 'top left' }
          ],
          { duration, easing }
        );
      });
    }

    animateAdaptiveTrackHeight(previousHeight) {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const nextHeight = this.activeMedia?.getBoundingClientRect().height || previousHeight;

        if (this.reducedMotion || Math.abs(nextHeight - previousHeight) < 1) {
          this.track.style.removeProperty('height');
          return;
        }

        this.track.style.height = `${nextHeight}px`;
        const onTransitionEnd = (event) => {
          if (event.target !== this.track || event.propertyName !== 'height') return;
          this.track.style.removeProperty('height');
          this.track.removeEventListener('transitionend', onTransitionEnd);
        };
        this.track.addEventListener('transitionend', onTransitionEnd);
        window.setTimeout(() => {
          this.track.style.removeProperty('height');
          this.track.removeEventListener('transitionend', onTransitionEnd);
        }, 520);
      });
    }

    syncSelection({ announce = false, settleMedia = false } = {}) {
      const visibleMedia = this.visibleMedia;
      if (visibleMedia.length === 0) return;

      if (!this.activeMedia || this.activeMedia.hidden) this.activeMedia = visibleMedia[0];
      const activeIndex = Math.max(0, visibleMedia.indexOf(this.activeMedia));
      const activeId = String(this.activeMedia.dataset.hzMediaId);

      this.style.setProperty('--hz-product-gallery-indicator-count', String(visibleMedia.length));
      this.style.setProperty(
        '--hz-product-gallery-indicator-gap',
        `${this.calculateIndicatorGap(visibleMedia.length)}px`
      );

      this.mediaItems.forEach((media) => {
        const visibleIndex = visibleMedia.indexOf(media);
        if (visibleIndex >= 0) {
          media.setAttribute('aria-label', this.formatItemLabel(visibleIndex + 1, visibleMedia.length));
        }
      });

      this.thumbnails.forEach((thumbnail) => {
        const media = this.mediaById.get(String(thumbnail.dataset.hzMediaId));
        const selected = media === this.activeMedia;
        const visibleIndex = media ? visibleMedia.indexOf(media) : -1;
        thumbnail.setAttribute('aria-current', selected ? 'true' : 'false');
        thumbnail.tabIndex = selected ? 0 : -1;
        if (visibleIndex >= 0) thumbnail.setAttribute('aria-label', this.formatSelectLabel(visibleIndex + 1));
      });
      this.ensureActiveThumbnailVisible();

      this.indicators.forEach((indicator) => {
        const media = this.mediaItems.find(
          (item) => String(item.dataset.hzMediaIndex) === String(indicator.dataset.hzMediaIndex)
        );
        const selected = media === this.activeMedia;
        const visibleIndex = media ? visibleMedia.indexOf(media) : -1;
        indicator.setAttribute('aria-current', selected ? 'true' : 'false');
        indicator.tabIndex = selected ? 0 : -1;
        if (visibleIndex >= 0) indicator.setAttribute('aria-label', this.formatSelectLabel(visibleIndex + 1));
      });

      const hasMultipleMedia = visibleMedia.length > 1;
      if (this.indicatorContainer) this.indicatorContainer.hidden = !hasMultipleMedia;
      if (this.thumbRail) this.thumbRail.hidden = !hasMultipleMedia;
      if (this.modalThumbViewport) this.modalThumbViewport.hidden = !hasMultipleMedia;

      this.modalThumbnails.forEach((thumbnail) => {
        const selected = String(thumbnail.dataset.hzMediaId) === activeId;
        thumbnail.setAttribute('aria-current', selected ? 'true' : 'false');
        thumbnail.tabIndex = selected ? 0 : -1;
      });

      this.syncImageSizes();
      this.syncMediaAccessibility();
      this.syncMediaInteractionButtons();
      this.syncVideoControls();
      this.syncIndicatorProgress({ forceActive: !this.isMobile });
      this.updateThumbnailControlState();
      this.updateArButton();
      this.syncModalControls();

      if (announce && this.status) {
        this.status.textContent = this.formatItemLabel(activeIndex + 1, visibleMedia.length);
      }
      if (settleMedia) this.settleActiveMedia({ announce: false });
    }

    calculateIndicatorGap(count) {
      if (count <= 1) return 0;
      return clamp((212 - count) / (count - 1), 0, 8);
    }

    formatSelectLabel(index) {
      return (this.dataset.hzSelectLabel || 'Go to item [index]').replaceAll('[index]', String(index));
    }

    formatItemLabel(index, count) {
      return (this.dataset.hzItemLabel || 'Item [index] of [count]')
        .replaceAll('[index]', String(index))
        .replaceAll('[count]', String(count));
    }

    formatActionLabel(template, index, count) {
      return (template || this.formatItemLabel(index, count))
        .replaceAll('[index]', String(index))
        .replaceAll('[count]', String(count));
    }

    syncImageSizes() {
      this.mediaItems.forEach((media) => {
        const mainSizes = media.dataset.hzMediaMainSizes;
        const secondarySizes = media.dataset.hzMediaSecondarySizes || mainSizes;
        if (!mainSizes) return;

        const useSecondarySizes =
          !this.isMobile
          && this.desktopLayout === 'highlighted_grid'
          && media !== this.activeMedia;
        const sizes = media === this.modalPlacement?.media
          ? this.modalImageSizes
          : useSecondarySizes
            ? secondarySizes
            : mainSizes;

        media.querySelectorAll(`${SELECTOR.mediaFrame} img[sizes]`).forEach((image) => {
          if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
        });
      });
    }

    syncMediaAccessibility() {
      const usesSingleActiveMedia = this.isMobile || this.desktopLayout.startsWith('thumbnails_');

      this.mediaItems.forEach((media) => {
        const isActive = media === this.activeMedia;
        const isInModal = media === this.modalPlacement?.media;
        const hideInactiveSlide = !media.hidden && usesSingleActiveMedia && !isActive && !isInModal;

        if (hideInactiveSlide) {
          media.setAttribute('aria-hidden', 'true');
        } else {
          media.removeAttribute('aria-hidden');
        }

        const embeddedMedia = media.querySelectorAll('video-media, model-media');
        const shouldInertEmbedded =
          !media.hidden &&
          media !== this.modalPlacement?.media &&
          !isActive &&
          (this.isMobile || this.desktopLayout !== 'stack');

        embeddedMedia.forEach((element) => {
          element.toggleAttribute('inert', shouldInertEmbedded);
        });
      });
    }

    syncMediaInteractionButtons() {
      const visibleMedia = this.visibleMedia;
      const visibleCount = Math.max(visibleMedia.length, 1);

      this.mediaItems.forEach((media) => {
        const mediaButton = media.querySelector(SELECTOR.mediaButton);
        const expandButton = media.querySelector(SELECTOR.expandButton);
        const isImage = media.dataset.hzMediaType === 'image';
        const isActive = media === this.activeMedia;
        const isVisible = !media.hidden;
        const visibleIndex = Math.max(visibleMedia.indexOf(media), 0) + 1;
        let showMediaButton = false;
        let showExpandButton = false;
        let operation = 'none';
        let controlRole = isActive ? 'main' : 'secondary';

        if (isVisible) {
          if (this.isMobile) {
            if (isActive && isImage) {
              showMediaButton = true;
              operation = 'gallery';
            } else if (isActive && !isImage) {
              showExpandButton = true;
            }
          } else if (this.desktopLayout === 'highlighted_grid') {
            if (isActive) {
              if (isImage) {
                showMediaButton = true;
                operation = 'gallery';
              } else {
                showExpandButton = true;
              }
            } else {
              showMediaButton = true;
              operation = this.highlightedSecondaryAction;
              controlRole = 'secondary';
            }
          } else if (this.desktopLayout === 'stack') {
            if (isImage && this.stackImageAction === 'gallery') {
              showMediaButton = true;
              operation = 'gallery';
            } else if (!isImage) {
              showExpandButton = true;
            }
            controlRole = 'main';
          } else if (isActive) {
            if (isImage) {
              showMediaButton = true;
              operation = 'gallery';
            } else {
              showExpandButton = true;
            }
          }
        }

        if (mediaButton) {
          mediaButton.hidden = !showMediaButton;
          mediaButton.dataset.hzGalleryOperation = operation;
          mediaButton.dataset.hzGalleryControlPurpose = operation;
          mediaButton.dataset.hzGalleryControlRole = controlRole;

          if (operation === 'promote') {
            mediaButton.setAttribute('aria-label', this.formatSelectLabel(visibleIndex));
            mediaButton.removeAttribute('aria-haspopup');
          } else if (operation === 'gallery') {
            mediaButton.setAttribute(
              'aria-label',
              this.formatActionLabel(this.dataset.hzGalleryLabel, visibleIndex, visibleCount)
            );
            mediaButton.setAttribute('aria-haspopup', 'dialog');
          } else {
            mediaButton.removeAttribute('aria-haspopup');
          }
        }

        if (expandButton) {
          expandButton.hidden = !showExpandButton;
          expandButton.dataset.hzGalleryOperation = 'gallery';
          expandButton.setAttribute(
            'aria-label',
            this.formatActionLabel(this.dataset.hzGalleryLabel, visibleIndex, visibleCount)
          );
          expandButton.setAttribute('aria-haspopup', 'dialog');
        }
      });
    }

    syncVideoControls() {
      this.videoToggles.forEach((toggle) => {
        const media = toggle.closest(SELECTOR.media);
        if (!media) return;
        const isActive = media === this.activeMedia;
        const isVisible = !media.hidden;
        const isInModal = media === this.modalPlacement?.media;
        const shouldShow = isVisible && (
          isInModal ||
          (this.isMobile && isActive) ||
          (!this.isMobile && this.desktopLayout === 'stack') ||
          (!this.isMobile && this.desktopLayout !== 'stack' && isActive)
        );
        toggle.hidden = !shouldShow;

        const videoElement = media.querySelector('video-media');
        this.syncVideoToggleState(media, Boolean(videoElement?.playing || videoElement?.hasAttribute('playing')));
      });
    }

    syncVideoToggleState(media, playing) {
      const toggle = media?.querySelector(SELECTOR.videoToggle);
      if (!toggle) return;
      toggle.dataset.hzState = playing ? 'pause' : 'play';
      toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      toggle.setAttribute(
        'aria-label',
        playing ? this.dataset.hzPauseLabel || 'Pause video' : this.dataset.hzPlayLabel || 'Play video'
      );
    }

    onNativeMediaStateEvent(event, playing) {
      const media = event.target instanceof Element ? event.target.closest(SELECTOR.media) : null;
      if (!media || !this.mediaItems.includes(media)) return;
      this.syncVideoToggleState(media, playing);
    }

    toggleVideo(media) {
      const videoElement = media?.querySelector('video-media');
      if (!videoElement) return;
      const isPlaying = Boolean(videoElement.playing || videoElement.hasAttribute('playing'));

      if (isPlaying) {
        videoElement.pause?.();
      } else {
        if (
          media !== this.activeMedia &&
          media !== this.modalPlacement?.media &&
          this.desktopLayout !== 'stack'
        ) {
          this.setActiveMedia(media, { animate: false, announce: true, settleMedia: false });
        }
        videoElement.play?.();
      }
    }

    executeMediaOperation(media, operation, opener) {
      if (!media || media.hidden) return;

      if (operation === 'promote') {
        this.selectMedia(media, { source: 'secondary', announce: true });
        return;
      }
      if (operation !== 'gallery') return;

      void this.openModal(media, opener);
    }

    scrollMediaIntoView(media, behavior = 'smooth') {
      if (!this.isMobile || !media) return;
      media.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : behavior,
        block: 'nearest',
        inline: 'start'
      });
    }

    syncMobilePosition({ behavior = 'auto' } = {}) {
      if (!this.isMobile || !this.activeMedia) return;
      this.scrollMediaIntoView(this.activeMedia, behavior);
    }

    settleActiveMedia({ announce = false } = {}) {
      if (!this.activeMedia) return;

      this.mediaItems.forEach((media) => {
        if (media !== this.activeMedia && media !== this.modalPlacement?.media) this.pauseMedia(media);
      });

      if (
        this.dataset.hzAutoplay === 'true' &&
        ['video', 'external_video'].includes(this.activeMedia.dataset.hzMediaType)
      ) {
        this.playMedia(this.activeMedia);
      } else if (!this.isMobile && this.activeMedia.dataset.hzMediaType === 'model') {
        this.playMedia(this.activeMedia);
      }
      this.syncVideoControls();

      if (announce && this.status) {
        const visibleMedia = this.visibleMedia;
        const index = visibleMedia.indexOf(this.activeMedia);
        if (index >= 0) this.status.textContent = this.formatItemLabel(index + 1, visibleMedia.length);
      }
    }

    settleModalMedia(media, { announce = false } = {}) {
      if (!media) return;

      this.mediaItems.forEach((item) => {
        if (item !== media) this.pauseMedia(item);
      });

      if (
        this.dataset.hzAutoplay === 'true' &&
        ['video', 'external_video'].includes(media.dataset.hzMediaType)
      ) {
        this.playMedia(media);
      } else if (media.dataset.hzMediaType === 'model') {
        this.playMedia(media);
      }
      this.syncVideoControls();

      if (announce && this.status) {
        const visibleMedia = this.visibleMedia;
        const index = visibleMedia.indexOf(media);
        if (index >= 0) this.status.textContent = this.formatItemLabel(index + 1, visibleMedia.length);
      }
    }

    playMedia(media) {
      const mediaElement = media?.querySelector('video-media, model-media');
      if (mediaElement && typeof mediaElement.play === 'function') mediaElement.play();
    }

    pauseMedia(media) {
      const mediaElement = media?.querySelector('video-media, model-media');
      if (mediaElement && typeof mediaElement.pause === 'function') mediaElement.pause();
    }

    updateArButton() {
      if (!this.arButton) return;
      const defaultId = this.arButton.dataset.shopifyModel3dDefaultId;
      const activeModelId = this.activeMedia?.dataset.hzMediaType === 'model' ? this.activeMedia.dataset.hzMediaId : defaultId;
      if (activeModelId) this.arButton.dataset.shopifyModel3dId = activeModelId;
    }

    syncResponsiveThumbnailOrientation() {
      if (!this.thumbList) return;
      this.thumbList.setAttribute('aria-orientation', this.usesVerticalThumbnails ? 'vertical' : 'horizontal');
    }

    ensureActiveThumbnailVisible() {
      if (!this.usesThumbnails || !this.thumbViewport || !this.activeMedia) return;

      const thumbnail = this.thumbnails.find(
        (item) => String(item.dataset.hzMediaId) === String(this.activeMedia.dataset.hzMediaId)
      );
      if (!thumbnail || thumbnail.hidden) return;

      const viewportRect = this.thumbViewport.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const behavior = this.reducedMotion ? 'auto' : 'smooth';

      if (this.usesVerticalThumbnails) {
        const delta = thumbnailRect.top < viewportRect.top
          ? thumbnailRect.top - viewportRect.top
          : thumbnailRect.bottom > viewportRect.bottom
            ? thumbnailRect.bottom - viewportRect.bottom
            : 0;
        if (Math.abs(delta) > 1) this.thumbViewport.scrollBy({ top: delta, behavior });
      } else {
        const delta = thumbnailRect.left < viewportRect.left
          ? thumbnailRect.left - viewportRect.left
          : thumbnailRect.right > viewportRect.right
            ? thumbnailRect.right - viewportRect.right
            : 0;
        if (Math.abs(delta) > 1) this.thumbViewport.scrollBy({ left: delta, behavior });
      }
    }

    syncVerticalThumbnailRailHeight() {
      if (!this.thumbRail) return;

      if (!this.usesVerticalThumbnails) {
        this.thumbRail.style.removeProperty('--hz-product-gallery-vertical-rail-height');
        return;
      }

      const trackHeight = Math.round(this.track?.getBoundingClientRect().height || 0);
      if (trackHeight <= 0) return;

      const nextValue = `${trackHeight}px`;
      if (this.thumbRail.style.getPropertyValue('--hz-product-gallery-vertical-rail-height') !== nextValue) {
        this.thumbRail.style.setProperty('--hz-product-gallery-vertical-rail-height', nextValue);
      }
    }

    scheduleThumbnailOverflowUpdate() {
      cancelAnimationFrame(this.overflowFrame);
      this.overflowFrame = requestAnimationFrame(() => {
        this.overflowFrame = 0;
        this.updateThumbnailOverflow();
      });
    }

    updateThumbnailOverflow() {
      this.syncVerticalThumbnailRailHeight();
      if (
        !this.usesThumbnails ||
        this.visibleThumbnails.length <= 1 ||
        !this.thumbRail ||
        !this.thumbViewport ||
        !this.thumbList ||
        !this.thumbControls
      ) {
        this.removeAttribute('data-hz-thumb-overflow');
        this.setThumbnailControlsAccessible(false);
        return;
      }

      const vertical = this.usesVerticalThumbnails;
      const railRect = this.thumbRail.getBoundingClientRect();
      const controlsRect = this.thumbControls.getBoundingClientRect();
      const reservedControlLength = vertical
        ? Math.max(40, controlsRect.height)
        : Math.max(88, controlsRect.width);
      const reservedGap = 12;
      const availableLength = Math.max(0, (vertical ? railRect.height : railRect.width) - reservedControlLength - reservedGap);
      const contentLength = vertical ? this.thumbList.scrollHeight : this.thumbList.scrollWidth;
      const hasOverflow = contentLength > availableLength + 1;

      this.toggleAttribute('data-hz-thumb-overflow', hasOverflow);
      if (hasOverflow) this.dataset.hzThumbOverflow = 'true';
      this.setThumbnailControlsAccessible(hasOverflow);
      requestAnimationFrame(() => this.updateThumbnailControlState());
    }

    setThumbnailControlsAccessible(enabled) {
      if (!this.thumbControls) return;
      this.thumbControls.setAttribute('aria-hidden', enabled ? 'false' : 'true');
      [this.thumbPrevious, this.thumbNext].filter(Boolean).forEach((button) => {
        button.tabIndex = enabled ? 0 : -1;
      });
    }

    updateThumbnailControlState() {
      if (this.dataset.hzThumbOverflow !== 'true' || !this.thumbViewport) {
        if (this.thumbPrevious) this.thumbPrevious.disabled = true;
        if (this.thumbNext) this.thumbNext.disabled = true;
        return;
      }

      const thumbnails = this.visibleThumbnails;
      if (thumbnails.length === 0) return;
      const viewportRect = this.thumbViewport.getBoundingClientRect();
      const firstRect = thumbnails[0].getBoundingClientRect();
      const lastRect = thumbnails[thumbnails.length - 1].getBoundingClientRect();
      const vertical = this.usesVerticalThumbnails;
      const firstVisible = vertical
        ? firstRect.top >= viewportRect.top - 1 && firstRect.bottom <= viewportRect.bottom + 1
        : firstRect.left >= viewportRect.left - 1 && firstRect.right <= viewportRect.right + 1;
      const lastVisible = vertical
        ? lastRect.top >= viewportRect.top - 1 && lastRect.bottom <= viewportRect.bottom + 1
        : lastRect.left >= viewportRect.left - 1 && lastRect.right <= viewportRect.right + 1;

      if (this.thumbPrevious) this.thumbPrevious.disabled = firstVisible;
      if (this.thumbNext) this.thumbNext.disabled = lastVisible;
    }

    pageThumbnails(direction) {
      if (this.dataset.hzThumbOverflow !== 'true' || !this.thumbViewport) return;

      const thumbnails = this.visibleThumbnails;
      const viewportRect = this.thumbViewport.getBoundingClientRect();
      const vertical = this.usesVerticalThumbnails;
      const currentIndex = Math.max(
        0,
        thumbnails.findIndex((thumbnail) => {
          const rect = thumbnail.getBoundingClientRect();
          return vertical ? rect.bottom > viewportRect.top + 1 : rect.right > viewportRect.left + 1;
        })
      );
      const firstSize = thumbnails[0]?.getBoundingClientRect();
      const itemLength = vertical ? firstSize?.height || 1 : firstSize?.width || 1;
      const viewportLength = vertical ? viewportRect.height : viewportRect.width;
      const pageSize = Math.max(1, Math.floor(viewportLength / Math.max(1, itemLength + 10)));
      const targetIndex = clamp(currentIndex + direction * pageSize, 0, thumbnails.length - 1);

      thumbnails[targetIndex]?.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : 'smooth',
        block: vertical ? 'start' : 'nearest',
        inline: vertical ? 'nearest' : 'start'
      });
    }

    async openModal(media, opener = null) {
      if (
        !this.modal ||
        !this.modalStage ||
        !media ||
        media.hidden ||
        this.modalOpening ||
        this.modalAnimating
      ) return;

      const openToken = ++this.modalOpenToken;
      this.modalOpening = true;

      try {
        await customElements.whenDefined('x-modal');
        if (!this.isConnected) return;

        const loadToken = ++this.modalLoadToken;
        const readyKey = String(media.dataset.hzMediaId || media.dataset.hzMediaIndex || '');
        const needsLoading = !this.modalReadyMediaIds.has(readyKey);
        this.modalOpener = opener instanceof HTMLElement ? opener : document.activeElement;
        this.removeMobileLoopClones();
        this.moveMediaToModal(media);
        this.syncModalControls();
        this.syncVideoControls();
        this.resetZoom(1);
        this.setModalLoading(needsLoading);

        await this.modal.show?.(!this.reducedMotion);
        if (needsLoading) {
          await this.waitForModalMediaReady(media);
          if (this.isModalMediaReady(media)) this.modalReadyMediaIds.add(readyKey);
        }
        if (loadToken !== this.modalLoadToken || !this.modal?.hasAttribute('open')) return;
        await this.finishModalLoading(loadToken);
        this.resetZoom(1);
        this.settleModalMedia(media, { announce: false });
        this.preloadAdjacentMedia(media);
      } catch (error) {
        if (openToken === this.modalOpenToken) {
          this.setModalLoading(false);
          if (!this.modal?.hasAttribute('open')) {
            this.restoreModalMedia();
            if (this.isMobile) this.refreshMobileLoop({ position: true });
          }
          console.error('Soleero product gallery could not open the media viewer.', error);
        }
      } finally {
        if (openToken === this.modalOpenToken) this.modalOpening = false;
      }
    }

    setModalLoading(loading) {
      if (!this.modal || !this.modalLoader) return;
      const isLoading = Boolean(loading);
      if (isLoading) this.modalLoadingStartedAt = performance.now();
      this.modal.toggleAttribute('data-hz-gallery-modal-loading', isLoading);
      this.modalLoader.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
      if (isLoading) {
        this.modalViewport?.setAttribute('aria-busy', 'true');
      } else {
        this.modalViewport?.removeAttribute('aria-busy');
      }
    }

    async finishModalLoading(loadToken) {
      if (!this.modalLoader || !this.modal?.hasAttribute('data-hz-gallery-modal-loading')) return;
      const elapsed = performance.now() - this.modalLoadingStartedAt;
      if (elapsed < 180) {
        await new Promise((resolve) => window.setTimeout(resolve, 180 - elapsed));
      }
      if (loadToken !== this.modalLoadToken) return;
      this.setModalLoading(false);
    }

    isImageReady(image) {
      if (!image?.complete || image.naturalWidth <= 0) return false;

      // Without srcset, `complete` may still describe the previously displayed
      // resource while a newly assigned `src` is pending. Do not dismiss the
      // loader until the requested resource is actually current.
      if (!image.getAttribute('srcset')) {
        const requestedSource = image.src || '';
        const currentSource = image.currentSrc || '';
        if (requestedSource && currentSource && requestedSource !== currentSource) return false;
      }

      return true;
    }

    isModalMediaReady(media) {
      if (!media) return false;
      const images = Array.from(media.querySelectorAll(`${SELECTOR.mediaFrame} img`));
      if (images.length === 0) return true;
      return images.every((image) => this.isImageReady(image));
    }

    waitForImage(image, timeout = 2400) {
      if (!image || (image.complete && image.naturalWidth === 0)) return Promise.resolve();
      if (this.isImageReady(image)) {
        return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
      }

      return new Promise((resolve) => {
        let timer = 0;
        const finish = () => {
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          window.clearTimeout(timer);
          if (this.isImageReady(image) && typeof image.decode === 'function') {
            image.decode().catch(() => {}).finally(resolve);
          } else {
            resolve();
          }
        };
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        timer = window.setTimeout(finish, timeout);
      });
    }

    async waitForModalMediaReady(media) {
      const waitForLayout = () => new Promise(
        (resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))
      );
      await waitForLayout();

      const images = Array.from(media?.querySelectorAll(`${SELECTOR.mediaFrame} img`) || []);
      await Promise.all(images.map(async (image) => {
        let previousSource = image.currentSrc || image.src || '';

        for (let attempt = 0; attempt < 3; attempt += 1) {
          await this.waitForImage(image);
          await waitForLayout();
          const currentSource = image.currentSrc || image.src || '';
          const ready = this.isImageReady(image);
          if (image.complete && image.naturalWidth === 0) break;
          if (ready && currentSource === previousSource) break;
          previousSource = currentSource;
        }
      }));

      await waitForLayout();
    }

    cancelAdjacentPreload() {
      if (!this.preloadIdleHandle) return;
      if (this.preloadUsesIdleCallback && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.preloadIdleHandle);
      } else {
        window.clearTimeout(this.preloadIdleHandle);
      }
      this.preloadIdleHandle = 0;
      this.preloadUsesIdleCallback = false;
    }

    preloadAdjacentMedia(currentMedia) {
      this.cancelAdjacentPreload();
      const run = () => {
        this.preloadIdleHandle = 0;
        if (!this.isConnected) return;
        const visibleMedia = this.visibleMedia;
        const currentIndex = visibleMedia.indexOf(currentMedia);
        if (currentIndex < 0) return;

        [visibleMedia[currentIndex - 1], visibleMedia[currentIndex + 1]]
          .filter(Boolean)
          .forEach((media) => {
            media.querySelectorAll(`${SELECTOR.mediaFrame} img`).forEach((image) => {
              image.loading = 'eager';
              if (typeof image.decode === 'function') void image.decode().catch(() => {});
            });
          });
      };

      if (typeof window.requestIdleCallback === 'function') {
        this.preloadUsesIdleCallback = true;
        this.preloadIdleHandle = window.requestIdleCallback(run, { timeout: 700 });
      } else {
        this.preloadUsesIdleCallback = false;
        this.preloadIdleHandle = window.setTimeout(run, 120);
      }
    }

    moveMediaToModal(media) {
      if (!this.modalStage || !media) return;
      if (this.modalPlacement?.media === media) return;
      this.restoreModalMedia();

      const rect = media.getBoundingClientRect();
      const placeholder = document.createElement('div');
      placeholder.className = `${media.className} hz__productGalleryMediaPlaceholder`;
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.style.height = `${Math.max(1, rect.height)}px`;
      placeholder.style.minHeight = `${Math.max(1, rect.height)}px`;

      const parent = media.parentNode;
      const nextSibling = media.nextSibling;
      parent?.insertBefore(placeholder, media);

      const modalImage = media.dataset.hzMediaType === 'image' ? media.querySelector('img') : null;
      const originalImageSizes = modalImage?.getAttribute('sizes') ?? null;
      const originalImageLoading = modalImage?.getAttribute('loading') ?? null;
      const originalImageDraggable = modalImage?.getAttribute('draggable') ?? null;
      if (modalImage) {
        modalImage.setAttribute('sizes', this.modalImageSizes);
        modalImage.setAttribute('loading', 'eager');
        modalImage.setAttribute('draggable', 'false');
      }

      media.setAttribute('data-hz-gallery-modal-media', '');
      this.modalStage.replaceChildren(media);
      this.modalPlacement = {
        media,
        placeholder,
        parent,
        nextSibling,
        modalImage,
        originalImageSizes,
        originalImageLoading,
        originalImageDraggable
      };
      media.removeAttribute('aria-hidden');
      media.querySelectorAll('video-media, model-media').forEach((element) => element.removeAttribute('inert'));
    }

    restoreModalMedia() {
      const placement = this.modalPlacement;
      if (!placement) return;

      const {
        media,
        placeholder,
        parent,
        nextSibling,
        modalImage,
        originalImageSizes,
        originalImageLoading,
        originalImageDraggable
      } = placement;
      media.removeAttribute('data-hz-gallery-modal-media');
      media.removeAttribute('data-hz-gallery-swiping');
      media.removeAttribute('data-hz-gallery-zoom-dragging');
      media.removeAttribute('data-hz-gallery-zoomed');
      media.removeAttribute('data-hz-gallery-pannable');
      media.style.removeProperty('--hz-product-gallery-swipe-x');
      media.style.removeProperty('--hz-product-gallery-zoom-scale');
      media.style.removeProperty('--hz-product-gallery-pan-x');
      media.style.removeProperty('--hz-product-gallery-pan-y');

      if (modalImage) {
        if (originalImageSizes === null) modalImage.removeAttribute('sizes');
        else modalImage.setAttribute('sizes', originalImageSizes);
        if (originalImageLoading === null) modalImage.removeAttribute('loading');
        else modalImage.setAttribute('loading', originalImageLoading);
        if (originalImageDraggable === null) modalImage.removeAttribute('draggable');
        else modalImage.setAttribute('draggable', originalImageDraggable);
      }

      if (placeholder?.isConnected) {
        placeholder.replaceWith(media);
      } else if (parent?.isConnected) {
        parent.insertBefore(media, nextSibling?.isConnected ? nextSibling : null);
      } else if (this.track?.isConnected) {
        const index = Number.parseInt(media.dataset.hzMediaIndex || '0', 10);
        const followingMedia = this.mediaItems.find(
          (candidate) => Number.parseInt(candidate.dataset.hzMediaIndex || '0', 10) > index && candidate.parentNode === this.track
        );
        this.track.insertBefore(media, followingMedia || null);
      }

      placeholder?.remove();
      this.modalPlacement = null;
      this.syncMediaDomOrder();
      this.syncMediaAccessibility();
      if (this.isMobile && !this.modal?.hasAttribute('open')) this.refreshMobileLoop({ position: false });
    }

    onModalAfterShow() {
      requestAnimationFrame(() => {
        this.resetZoom(1);
        this.updatePannableState();
      });
    }

    onModalBeforeHide() {
      this.modalTransitionToken += 1;
      this.modalLoadToken += 1;
      this.modalOpenToken += 1;
      this.modalOpening = false;
      this.modalAnimating = false;
      this.setModalLoading(false);
      this.cancelAdjacentPreload();
      this.pauseMedia(this.modalPlacement?.media);
      this.pointers.clear();
      this.pointerGesture = null;
    }

    onModalAfterHide() {
      this.restoreModalMedia();
      this.resetZoom(1);
      if (this.isMobile) this.refreshMobileLoop({ position: false });
      this.syncSelection({ announce: false, settleMedia: true });
      this.syncMobilePosition({ behavior: 'auto' });
      this.syncIndicatorProgress({ forceActive: !this.isMobile });

      const opener = this.modalOpener;
      this.modalOpener = null;
      requestAnimationFrame(() => {
        if (opener?.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      });
    }

    onModalClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const videoToggle = target.closest(SELECTOR.videoToggle);
      if (videoToggle) {
        event.preventDefault();
        event.stopPropagation();
        const media = videoToggle.closest(SELECTOR.media);
        if (media) this.toggleVideo(media);
        return;
      }

      const previous = target.closest(SELECTOR.modalPrevious);
      const next = target.closest(SELECTOR.modalNext);
      const thumbnail = target.closest(SELECTOR.modalThumbnail);

      if (previous) {
        event.preventDefault();
        this.navigateModal(-1);
        return;
      }
      if (next) {
        event.preventDefault();
        this.navigateModal(1);
        return;
      }
      if (thumbnail) {
        event.preventDefault();
        const media = this.mediaById.get(String(thumbnail.dataset.hzMediaId));
        if (media) void this.showModalMedia(media);
        return;
      }
      if (target.closest(SELECTOR.zoomOut)) {
        event.preventDefault();
        this.setZoom(this.zoomScale - 0.5);
        return;
      }
      if (target.closest(SELECTOR.zoomIn)) {
        event.preventDefault();
        this.setZoom(this.zoomScale + 0.5);
        return;
      }
      if (target.closest(SELECTOR.zoomReset)) {
        event.preventDefault();
        this.resetZoom(1);
      }
    }

    onModalKeydown(event) {
      if (!this.modal?.hasAttribute('open')) return;
      if (
        event.target instanceof Element &&
        event.target.closest('input, textarea, select, video-media, model-media, model-viewer, video, iframe')
      ) return;

      const rtl = document.documentElement.dir === 'rtl';
      const modalThumbnail = event.target instanceof Element
        ? event.target.closest(SELECTOR.modalThumbnail)
        : null;

      if (modalThumbnail) {
        const visibleThumbnails = this.modalThumbnails.filter((thumbnail) => !thumbnail.hidden);
        const currentIndex = visibleThumbnails.indexOf(modalThumbnail);
        const previousKey = rtl ? 'ArrowRight' : 'ArrowLeft';
        const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';

        if (currentIndex >= 0 && [previousKey, nextKey, 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          let nextIndex = currentIndex;
          if (event.key === previousKey) nextIndex = Math.max(0, currentIndex - 1);
          if (event.key === nextKey) nextIndex = Math.min(visibleThumbnails.length - 1, currentIndex + 1);
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = visibleThumbnails.length - 1;

          const nextThumbnail = visibleThumbnails[nextIndex];
          nextThumbnail?.focus({ preventScroll: true });
          const media = nextThumbnail ? this.mediaById.get(String(nextThumbnail.dataset.hzMediaId)) : null;
          if (media && media !== this.modalPlacement?.media) {
            void this.showModalMedia(media, nextIndex > currentIndex ? 1 : -1);
          }
          return;
        }
      }

      const arrowPan = {
        ArrowLeft: { x: 48, y: 0 },
        ArrowRight: { x: -48, y: 0 },
        ArrowUp: { x: 0, y: 48 },
        ArrowDown: { x: 0, y: -48 }
      };

      const panBounds = this.modalImage ? this.getPanBounds() : { x: 0, y: 0 };
      const canPanForKey = arrowPan[event.key] && (
        (arrowPan[event.key].x !== 0 && panBounds.x > 0.5) ||
        (arrowPan[event.key].y !== 0 && panBounds.y > 0.5)
      );

      if (this.modalImage && canPanForKey) {
        event.preventDefault();
        this.panX += arrowPan[event.key].x;
        this.panY += arrowPan[event.key].y;
        this.clampPan();
        this.applyZoomStyles();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigateModal(rtl ? 1 : -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigateModal(rtl ? -1 : 1);
      } else if ((event.key === '+' || event.key === '=') && this.modalImage) {
        event.preventDefault();
        this.setZoom(this.zoomScale + 0.5);
      } else if (event.key === '-' && this.modalImage) {
        event.preventDefault();
        this.setZoom(this.zoomScale - 0.5);
      } else if (event.key === '0' && this.modalImage) {
        event.preventDefault();
        this.resetZoom(1);
      }
    }

    navigateModal(direction) {
      if (this.modalOpening || this.modalAnimating) return;
      const visibleMedia = this.visibleMedia;
      const currentMedia = this.modalPlacement?.media || this.activeMedia;
      const currentIndex = visibleMedia.indexOf(currentMedia);
      const nextIndex = clamp(currentIndex + direction, 0, visibleMedia.length - 1);
      if (nextIndex === currentIndex || nextIndex < 0) return;
      void this.showModalMedia(visibleMedia[nextIndex], direction);
    }

    async showModalMedia(media, direction = 0) {
      if (
        !media ||
        media.hidden ||
        media === this.modalPlacement?.media ||
        this.modalOpening ||
        this.modalAnimating
      ) return;

      const transitionToken = ++this.modalTransitionToken;
      const loadToken = ++this.modalLoadToken;
      this.modalAnimating = true;
      const stage = this.modalStage;
      const visualDirection = direction || (
        this.visibleMedia.indexOf(media) > this.visibleMedia.indexOf(this.modalPlacement?.media) ? 1 : -1
      );
      const directionFactor = document.documentElement.dir === 'rtl' ? -visualDirection : visualDirection;
      const readyKey = String(media.dataset.hzMediaId || media.dataset.hzMediaIndex || '');
      const needsLoading = !this.modalReadyMediaIds.has(readyKey);
      let exitAnimation = null;
      let enterAnimation = null;

      try {
        if (!this.reducedMotion && stage?.animate) {
          exitAnimation = stage.animate(
            [
              { opacity: 1, transform: 'translate3d(0, 0, 0)' },
              { opacity: 0, transform: `translate3d(${-directionFactor * 28}px, 0, 0)` }
            ],
            { duration: 150, easing: 'ease-in', fill: 'forwards' }
          );
          await exitAnimation.finished.catch(() => {});
        }

        if (transitionToken !== this.modalTransitionToken || !this.modal?.hasAttribute('open')) return;
        this.setModalLoading(needsLoading);

        this.pauseMedia(this.modalPlacement?.media);
        this.restoreModalMedia();
        this.moveMediaToModal(media);
        this.resetZoom(1);
        this.syncModalControls();
        this.syncVideoControls();

        if (needsLoading) {
          await this.waitForModalMediaReady(media);
          if (this.isModalMediaReady(media)) this.modalReadyMediaIds.add(readyKey);
        }
        if (
          transitionToken !== this.modalTransitionToken ||
          loadToken !== this.modalLoadToken ||
          !this.modal?.hasAttribute('open')
        ) return;
        await this.finishModalLoading(loadToken);
        this.settleModalMedia(media, { announce: true });

        if (!this.reducedMotion && stage?.animate) {
          enterAnimation = stage.animate(
            [
              { opacity: 0, transform: `translate3d(${directionFactor * 28}px, 0, 0)` },
              { opacity: 1, transform: 'translate3d(0, 0, 0)' }
            ],
            { duration: 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
          );
          await enterAnimation.finished.catch(() => {});
        }
        this.preloadAdjacentMedia(media);
      } finally {
        exitAnimation?.cancel();
        enterAnimation?.cancel();
        if (stage) {
          stage.style.removeProperty('opacity');
          stage.style.removeProperty('transform');
        }
        if (transitionToken === this.modalTransitionToken || !this.modal?.hasAttribute('open')) {
          this.modalAnimating = false;
        }
      }
    }

    syncModalControls() {
      if (!this.modal) return;
      const currentMedia = this.modalPlacement?.media || this.activeMedia;
      const visibleMedia = this.visibleMedia;
      const currentIndex = Math.max(0, visibleMedia.indexOf(currentMedia));
      const mediaType = currentMedia?.dataset.hzMediaType || 'image';

      this.modal.dataset.hzGalleryModalMediaType = mediaType;
      if (this.modalViewport) this.modalViewport.dataset.hzGalleryModalMediaType = mediaType;
      if (this.modalCounter) this.modalCounter.textContent = `${currentIndex + 1} / ${Math.max(1, visibleMedia.length)}`;

      if (this.modalPrevious) {
        this.modalPrevious.hidden = visibleMedia.length <= 1;
        this.modalPrevious.disabled = currentIndex <= 0;
      }
      if (this.modalNext) {
        this.modalNext.hidden = visibleMedia.length <= 1;
        this.modalNext.disabled = currentIndex >= visibleMedia.length - 1;
      }
      if (this.modalThumbViewport) this.modalThumbViewport.hidden = visibleMedia.length <= 1;

      this.modalThumbnails.forEach((thumbnail) => {
        const selected = String(thumbnail.dataset.hzMediaId) === String(currentMedia?.dataset.hzMediaId);
        thumbnail.setAttribute('aria-current', selected ? 'true' : 'false');
        thumbnail.tabIndex = selected ? 0 : -1;
        const media = this.mediaById.get(String(thumbnail.dataset.hzMediaId));
        const visibleIndex = media ? visibleMedia.indexOf(media) : -1;
        if (visibleIndex >= 0) thumbnail.setAttribute('aria-label', this.formatSelectLabel(visibleIndex + 1));
        if (selected && this.modal?.hasAttribute('open')) {
          thumbnail.scrollIntoView({
            behavior: this.reducedMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      });

      this.updatePannableState();
      this.updateZoomControls();
      this.syncVideoControls();
    }

    get modalImage() {
      if (this.modalPlacement?.media?.dataset.hzMediaType !== 'image') return null;
      return this.modalPlacement.media.querySelector(`${SELECTOR.mediaFrame} img`);
    }

    onModalWheel(event) {
      if (!this.modalImage || !this.modal?.hasAttribute('open')) return;
      event.preventDefault();
      const viewportRect = this.modalViewport.getBoundingClientRect();
      const anchorX = event.clientX - (viewportRect.left + viewportRect.width / 2);
      const anchorY = event.clientY - (viewportRect.top + viewportRect.height / 2);
      const increment = event.deltaY < 0 ? 0.2 : -0.2;
      this.setZoom(this.zoomScale + increment, { anchorX, anchorY });
    }

    onModalDoubleClick(event) {
      if (!this.modalImage) return;
      event.preventDefault();
      const viewportRect = this.modalViewport.getBoundingClientRect();
      const anchorX = event.clientX - (viewportRect.left + viewportRect.width / 2);
      const anchorY = event.clientY - (viewportRect.top + viewportRect.height / 2);
      const targetScale = this.zoomScale > 1.05 ? 1 : Math.min(2, this.maxZoom);
      this.setZoom(targetScale, { anchorX, anchorY });
    }

    setZoom(nextScale, { anchorX = 0, anchorY = 0 } = {}) {
      if (!this.modalImage) return;
      const previousScale = this.zoomScale || 1;
      const clampedScale = clamp(nextScale, 1, this.maxZoom);
      const ratio = clampedScale / previousScale;

      if (clampedScale === 1) {
        this.panX = 0;
        this.panY = 0;
      } else if (ratio !== 1) {
        this.panX = ratio * this.panX + (1 - ratio) * anchorX;
        this.panY = ratio * this.panY + (1 - ratio) * anchorY;
      }

      this.zoomScale = clampedScale;
      this.clampPan();
      this.applyZoomStyles();
      this.updateZoomControls();
    }

    resetZoom(scale = 1) {
      this.zoomScale = clamp(scale, 1, this.maxZoom);
      this.panX = 0;
      this.panY = 0;
      this.applyZoomStyles();
      this.updateZoomControls();
    }

    getPanBounds() {
      const image = this.modalImage;
      const frame = image?.closest(SELECTOR.mediaFrame);
      if (!image || !frame) return { x: 0, y: 0 };

      const frameWidth = frame.clientWidth;
      const frameHeight = frame.clientHeight;
      const naturalWidth = image.naturalWidth || frameWidth;
      const naturalHeight = image.naturalHeight || frameHeight;
      if (!frameWidth || !frameHeight || !naturalWidth || !naturalHeight) return { x: 0, y: 0 };

      const imageRatio = naturalWidth / naturalHeight;
      const frameRatio = frameWidth / frameHeight;
      const renderedWidth = imageRatio >= frameRatio ? frameWidth : frameHeight * imageRatio;
      const renderedHeight = imageRatio >= frameRatio ? frameWidth / imageRatio : frameHeight;

      return {
        x: Math.max(0, (renderedWidth * this.zoomScale - frameWidth) / 2),
        y: Math.max(0, (renderedHeight * this.zoomScale - frameHeight) / 2)
      };
    }

    canPanImage() {
      const bounds = this.getPanBounds();
      return bounds.x > 0.5 || bounds.y > 0.5;
    }

    clampPan() {
      const bounds = this.getPanBounds();
      this.panX = clamp(this.panX, -bounds.x, bounds.x);
      this.panY = clamp(this.panY, -bounds.y, bounds.y);
    }

    updatePannableState() {
      const media = this.modalPlacement?.media;
      const pannable = Boolean(this.modalImage && this.canPanImage());
      media?.toggleAttribute('data-hz-gallery-pannable', pannable);
      this.modalViewport?.toggleAttribute('data-hz-gallery-pannable', pannable);
    }

    applyZoomStyles() {
      const media = this.modalPlacement?.media;
      if (!media) return;
      media.toggleAttribute('data-hz-gallery-zoomed', this.zoomScale > 1.001);
      media.style.setProperty('--hz-product-gallery-zoom-scale', String(this.zoomScale));
      media.style.setProperty('--hz-product-gallery-pan-x', `${this.panX}px`);
      media.style.setProperty('--hz-product-gallery-pan-y', `${this.panY}px`);
      this.updatePannableState();
    }

    updateZoomControls() {
      const hasImage = Boolean(this.modalImage);
      if (this.zoomControls) this.zoomControls.hidden = !hasImage;
      if (this.zoomOutButton) this.zoomOutButton.disabled = !hasImage || this.zoomScale <= 1.001;
      if (this.zoomInButton) this.zoomInButton.disabled = !hasImage || this.zoomScale >= this.maxZoom - 0.001;
      if (this.zoomValue) this.zoomValue.textContent = `${Math.round(this.zoomScale * 100)}%`;
    }

    onModalPointerDown(event) {
      if (!this.modalImage || event.button > 0) return;
      event.preventDefault();
      this.modalViewport.setPointerCapture?.(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        const points = Array.from(this.pointers.values());
        this.pointerGesture = 'pinch';
        this.pinchStart = {
          distance: Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y)),
          scale: this.zoomScale,
          panX: this.panX,
          panY: this.panY
        };
        this.modalPlacement?.media?.setAttribute('data-hz-gallery-zoom-dragging', 'true');
        return;
      }

      this.pointerGesture = this.canPanImage() ? 'pan' : 'swipe';
      this.singlePointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
        panX: this.panX,
        panY: this.panY
      };
      if (this.pointerGesture === 'pan') {
        this.modalPlacement?.media?.setAttribute('data-hz-gallery-zoom-dragging', 'true');
      } else {
        this.modalPlacement?.media?.setAttribute('data-hz-gallery-swiping', 'true');
      }
    }

    onModalPointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.modalImage) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size >= 2 && this.pinchStart) {
        event.preventDefault();
        const points = Array.from(this.pointers.values()).slice(0, 2);
        const distance = Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y));
        const nextScale = this.pinchStart.scale * (distance / this.pinchStart.distance);
        this.setZoom(nextScale);
        return;
      }

      if (!this.singlePointerStart || this.singlePointerStart.id !== event.pointerId) return;
      const deltaX = event.clientX - this.singlePointerStart.x;
      const deltaY = event.clientY - this.singlePointerStart.y;

      if (this.pointerGesture === 'pan') {
        event.preventDefault();
        this.panX = this.singlePointerStart.panX + deltaX;
        this.panY = this.singlePointerStart.panY + deltaY;
        this.clampPan();
        this.applyZoomStyles();
      } else if (this.pointerGesture === 'swipe') {
        event.preventDefault();
        this.modalPlacement?.media?.style.setProperty('--hz-product-gallery-swipe-x', `${deltaX}px`);
      }
    }

    onModalPointerEnd(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const endingPoint = this.pointers.get(event.pointerId);
      this.pointers.delete(event.pointerId);
      this.modalViewport.releasePointerCapture?.(event.pointerId);

      if (this.pointerGesture === 'swipe' && this.singlePointerStart) {
        const deltaX = endingPoint.x - this.singlePointerStart.x;
        const elapsed = Math.max(1, performance.now() - this.singlePointerStart.time);
        const velocity = Math.abs(deltaX) / elapsed;
        const threshold = Math.max(48, (this.modalViewport?.clientWidth || 320) * 0.12);
        const shouldNavigate = Math.abs(deltaX) >= threshold || velocity > 0.55;
        const rtlFactor = document.documentElement.dir === 'rtl' ? -1 : 1;
        const direction = deltaX < 0 ? 1 * rtlFactor : -1 * rtlFactor;

        this.modalPlacement?.media?.removeAttribute('data-hz-gallery-swiping');
        this.modalPlacement?.media?.style.setProperty('--hz-product-gallery-swipe-x', '0px');
        if (shouldNavigate) this.navigateModal(direction);
      }

      if (this.pointers.size === 1) {
        const [pointerId, point] = this.pointers.entries().next().value;
        this.pointerGesture = this.canPanImage() ? 'pan' : 'swipe';
        this.singlePointerStart = {
          id: pointerId,
          x: point.x,
          y: point.y,
          time: performance.now(),
          panX: this.panX,
          panY: this.panY
        };
        this.pinchStart = null;
        return;
      }

      if (this.pointers.size === 0) {
        this.modalPlacement?.media?.removeAttribute('data-hz-gallery-zoom-dragging');
        this.pointerGesture = null;
        this.singlePointerStart = null;
        this.pinchStart = null;
        this.clampPan();
        this.applyZoomStyles();
      }
    }
  }

  if (!customElements.get('ar-product-gallery')) {
    customElements.define('ar-product-gallery', ArProductGallery);
  }
})();
